import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Keyboard } from "lucide-react";
import { useShortcut } from "@/lib/keyboard/use-shortcut";
import { Button } from "@/components/ui/button";

interface Shortcut {
  keys: string[];
  description: string;
}

const SHORTCUTS: { section: string; items: Shortcut[] }[] = [
  {
    section: "Toàn cục",
    items: [
      { keys: ["?"], description: "Hiện danh sách phím tắt" },
      { keys: ["Esc"], description: "Đóng dialog / huỷ" },
    ],
  },
  {
    section: "Màn hình POS",
    items: [
      { keys: ["F2"], description: "Tìm kiếm sản phẩm" },
      { keys: ["F8"], description: "Xoá giỏ hàng" },
      { keys: ["F9"], description: "Thanh toán tiền mặt" },
      { keys: ["F11"], description: "Thanh toán QR" },
    ],
  },
  {
    section: "Điều hướng",
    items: [
      { keys: ["Alt", "P"], description: "Mở POS" },
      { keys: ["Alt", "O"], description: "Danh sách đơn hàng" },
    ],
  },
];

function KeyBadge({ k }: { k: string }) {
  return (
    <kbd className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs">
      {k}
    </kbd>
  );
}

/** The dialog itself — rendered via portal so it's always on top. */
function Dialog({ onClose }: { onClose: () => void }) {
  useShortcut("Escape", onClose);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-lg border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-semibold">
            <Keyboard size={18} />
            Phím tắt
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Đóng">
            <X size={16} />
          </Button>
        </div>

        <div className="space-y-4">
          {SHORTCUTS.map((group) => (
            <div key={group.section}>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {group.section}
              </p>
              <ul className="space-y-1.5">
                {group.items.map((s) => (
                  <li key={s.description} className="flex items-center justify-between text-sm">
                    <span>{s.description}</span>
                    <span className="flex items-center gap-1">
                      {s.keys.map((k) => <KeyBadge key={k} k={k} />)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Nhấn <KeyBadge k="?" /> hoặc <KeyBadge k="Esc" /> để đóng
        </p>
      </div>
    </div>,
    document.body,
  );
}

/**
 * KeyboardHelpDialog — mounts the ? trigger globally.
 * Place once in AppShell. The dialog opens with ? and closes with Esc.
 */
export function KeyboardHelpDialog() {
  const [open, setOpen] = useState(false);
  useShortcut("?", () => setOpen((o) => !o));

  return open ? <Dialog onClose={() => setOpen(false)} /> : null;
}
